// Projects API service

const API_BASE_URL = () => {
  // Check if we're in production
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL || 'https://findashback.onrender.com';
  }
  
  // Development environment
  return process.env.REACT_APP_API_URL || 'http://localhost:5000';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token found');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export interface Project {
  _id?: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: 'High' | 'Medium' | 'Low';
  status: 'Planning' | 'Active' | 'On Hold' | 'Completed' | 'Cancelled';
  startDate?: string;
  endDate?: string;
  budget?: number;
  progress?: number;
  tasks?: Task[];
  sourceIdeaId?: string; // Reference to original idea
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Task {
  _id?: string;
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: 'High' | 'Medium' | 'Low';
  status: 'Todo' | 'In Progress' | 'Review' | 'Completed' | 'Blocked';
  estimatedHours?: number;
  actualHours?: number;
  assignee?: string;
  dueDate?: string;
  projectId?: string; // Reference to parent project
  sourceProjectId?: string; // Reference to original project
  userId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectStats {
  total: number;
  active: number;
  completed: number;
  onHold: number;
  planning: number;
}

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  completed: number;
  blocked: number;
}

export interface FunnelStats {
  ideas: number;
  projects: number;
  tasks: number;
  totalCompleted: number;
  tasksFromIdeas?: number;
  tasksFromProjects?: number;
  completedTasksFromIdeas?: number;
  completedTasksFromProjects?: number;
}

export interface PromotionData {
  sourceId: string;
  fromStage: 'idea' | 'project';
  toStage: 'project' | 'task';
  title: string;
  description: string;
  category: string;
  tags: string[];
  priority: 'High' | 'Medium' | 'Low';
  // Project-specific fields
  startDate?: string;
  endDate?: string;
  budget?: number;
  status?: string;
  // Task-specific fields
  estimatedHours?: number;
  assignee?: string;
  dueDate?: string;
  taskStatus?: string;
  projectId?: string;
}

export const projectsApi = {
  // Fetch all projects
  async fetchProjects(): Promise<Project[]> {
    try {
      const response = await fetch(`${API_BASE_URL()}/personal-projects`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error;
    }
  },

  // Create a new project
  async createProject(projectData: Omit<Project, '_id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL()}/personal-projects`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(projectData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating project:', error);
      throw error;
    }
  },

  // Update an existing project
  async updateProject(projectId: string, updates: Partial<Project>): Promise<Project> {
    try {
      const response = await fetch(`${API_BASE_URL()}/personal-projects/${projectId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating project:', error);
      throw error;
    }
  },

  // Delete a project
  async deleteProject(projectId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/personal-projects/${projectId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      throw error;
    }
  },

  // Get project statistics
  async getStats(): Promise<ProjectStats> {
    try {
      const response = await fetch(`${API_BASE_URL()}/personal-projects/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching project stats:', error);
      throw error;
    }
  }
};

export const tasksApi = {
  // Fetch all tasks
  async fetchTasks(projectId?: string): Promise<Task[]> {
    try {
      const url = projectId 
        ? `${API_BASE_URL()}/tasks?projectId=${projectId}`
        : `${API_BASE_URL()}/tasks`;
        
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  // Create a new task
  async createTask(taskData: Omit<Task, '_id' | 'userId' | 'createdAt' | 'updatedAt'>): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL()}/tasks`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(taskData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  // Update an existing task
  async updateTask(taskId: string, updates: Partial<Task>): Promise<Task> {
    try {
      const response = await fetch(`${API_BASE_URL()}/tasks/${taskId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  },

  // Delete a task
  async deleteTask(taskId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL()}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      throw error;
    }
  },

  // Get task statistics
  async getStats(): Promise<TaskStats> {
    try {
      const response = await fetch(`${API_BASE_URL()}/tasks/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching task stats:', error);
      throw error;
    }
  }
};

export const funnelApi = {
  // Promote an item from one stage to the next
  async promoteItem(promotionData: PromotionData): Promise<{ success: boolean; newItem: Project | Task; sourceUpdated?: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL()}/funnel/promote`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(promotionData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error promoting item:', error);
      throw error;
    }
  },

  // Get overall funnel statistics
  async getFunnelStats(): Promise<FunnelStats> {
    try {
      const response = await fetch(`${API_BASE_URL()}/funnel/stats`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching funnel stats:', error);
      throw error;
    }
  },

  // Get promotion history for tracking
  async getPromotionHistory(): Promise<any[]> {
    try {
      const response = await fetch(`${API_BASE_URL()}/funnel/history`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching promotion history:', error);
      throw error;
    }
  }
};
